import { useState, useRef, useEffect } from "react";
import "./JDSkillsPage.css";
import { exportTablesToXls, hasExportableTable } from "./tableXlsExport";
import JobSpecComparison from "./JobSpecComparison";
import SkillsMatrixComparison from "./SkillsMatrixComparison";

/**
 * JDSkillsPage
 *
 * The JD & Skills Comparison view (one rail item in Shell.jsx). Frame ported
 * from Minerva_intelligence_UI_prototype.html's #view-skills: hero + export +
 * two comparison tools.
 *
 * Per instruction, the two tools are NOT side by side - they're TABS (like
 * the comp page's chart tabs). Default = Job Descriptions Comparison, full
 * page; click Skills Matrix Comparison and it takes the full page instead.
 *
 * Job Descriptions Comparison is built (ported as-is, incl. the existing AI
 * summary). Skills Matrix Comparison is a placeholder for now - next build.
 */
const TABS = [
  { key: "jd", label: "Job Descriptions Comparison" },
  { key: "skills", label: "Skills Matrix Comparison" },
];

export default function JDSkillsPage() {
  const [activeTab, setActiveTab] = useState("jd");
  const contentRef = useRef(null);
  const [canExport, setCanExport] = useState(false);

  // XLS is enabled only while the active tab is actually showing a table
  // (tables appear/disappear with the tab's own selections and toggles, so
  // watch the DOM rather than lifting every tab's state up here).
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;
    const update = () => setCanExport(hasExportableTable(root));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="jds-page">
      <div className="jds-hero">
        <div className="jds-hero-left">
          {/* Decorative leading dash removed (18-Aug: reads as an em dash). */}
          <div className="jds-eyebrow">
            MINERVA INTELLIGENCE &middot; COMPARISON TOOLS
          </div>
          <h1 className="jds-title">
            Job description &amp; <em>skills matrix</em> comparison.
          </h1>
          <p className="jds-desc">
            Two comparison tools sharing the same data spine as the rest of
            Minerva: line up job descriptions side by side, and align skills
            matrices across roles.
          </p>
        </div>

        {/* Export controls. XLS mirrors the table(s) on screen for whichever
            tab is active - see tableXlsExport.js. PDF not built yet. */}
        <div className="jds-export">
          <span className="jds-export-lbl">Export</span>
          <button type="button" className="jds-exp-btn" data-fmt="PDF" disabled title="Export not available yet">
            <PdfIcon /><span>PDF</span>
          </button>
          <button
            type="button"
            className="jds-exp-btn"
            data-fmt="XLS"
            disabled={!canExport}
            title={canExport ? "Export the table shown below to Excel" : "Select roles to see a table, then export it"}
            onClick={() => exportTablesToXls(contentRef.current)}
          >
            <XlsIcon /><span>XLS</span>
          </button>
        </div>
      </div>

      <div className="jds-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`jds-tab${activeTab === tab.key ? " active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="jds-tab-content" ref={contentRef}>
        {activeTab === "jd" && <JobSpecComparison />}
        {activeTab === "skills" && <SkillsMatrixComparison />}
      </div>
    </div>
  );
}

function PdfIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function XlsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
    </svg>
  );
}
